import React, { useEffect, lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { checkAuthStatus } from './store/slices/authSlice';

// Layout Components
import Layout from './components/Layout/Layout';

// Public Pages
import HomePage from './pages/Public/HomePage';
import ProductsPage from './pages/Public/ProductsPage';
import ProductDetailPage from './pages/Public/ProductDetailPage';
import GalleryPage from './pages/Public/GalleryPage';
import GalleryViewPage from './pages/Public/GalleryViewPage';
import ExperiencesPage from './pages/Public/ExperiencesPage';
import FarmStayPage from './pages/Public/FarmStayPage';
import FarmStayBookingPage from './pages/Public/FarmStayBookingPage';
import ContactPage from './pages/Public/ContactPage';
import CartPage from './pages/Public/CartPage';
import FavoritesPage from './pages/FavoritesPage';
// import CheckoutPage from './pages/Public/CheckoutPage';
import SimpleCheckout from './pages/Public/SimpleCheckout';
import BookingPage from './pages/Public/BookingPage';
import BlogPage from './pages/Public/BlogPage';
import BlogDetailPage from './pages/Public/BlogDetailPage';

// Customer Pages

// Worker Pages

// Admin Pages

// Auth Components
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ConfirmSignup from './components/Auth/ConfirmSignup';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Common Components
import ScrollToTop from './components/Common/ScrollToTop';

const WeeklyTaskTemplate = lazy(() => import('./components/Tasks/WeeklyTaskTemplate'));
const TaskTemplateManager = lazy(() => import('./components/Tasks/TaskTemplateManager'));
const MachineryManagement = lazy(() => import('./components/Machinery/MachineryManagement'));
const MachineryDetail = lazy(() => import('./components/Machinery/MachineryDetail'));
const MachineryDashboard = lazy(() => import('./components/Machinery/MachineryDashboard'));
// Staff areas are lazy: they drag in chart.js, recharts and the date pickers,
// which were all sitting in the single 2.9 MB chunk every guest downloaded.
// Our Story is the one lazy public route: it pulls in its own stylesheet and
// a timeline of media that no other page touches, so it should not sit in the
// main bundle for someone who came to buy pickle.
const AboutPage = lazy(() => import('./pages/Public/AboutPage'));

const AdminLayout = lazy(() => import('./components/Layout/AdminLayout'));
const CustomerDashboard = lazy(() => import('./pages/Customer/CustomerDashboard'));
const MyOrders = lazy(() => import('./pages/Customer/MyOrders'));
const MyBookings = lazy(() => import('./pages/Customer/MyBookings'));
const CustomerProfile = lazy(() => import('./pages/Customer/CustomerProfile'));
const WorkerDashboard = lazy(() => import('./pages/Worker/WorkerDashboard'));
const TaskManager = lazy(() => import('./pages/Worker/TaskManager'));
const ProductionTracker = lazy(() => import('./pages/Worker/ProductionTracker'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const UserManagement = lazy(() => import('./pages/Admin/UserManagement'));
const ProductManagement = lazy(() => import('./pages/Admin/ProductManagement'));
const ProductForm = lazy(() => import('./pages/Admin/ProductForm'));
const BlogManagement = lazy(() => import('./pages/Admin/BlogManagement'));
const BlogEditPage = lazy(() => import('./pages/Admin/BlogEditPage'));
const NewsletterManagement = lazy(() => import('./pages/Admin/NewsletterManagement'));
const FeaturedProductsManagement = lazy(() => import('./pages/Admin/FeaturedProductsManagement'));
const CategoryManagement = lazy(() => import('./pages/Admin/CategoryManagement'));
const GalleryManagement = lazy(() => import('./pages/Admin/GalleryManagement'));
const LandingManagement = lazy(() => import('./pages/Admin/LandingManagement'));
const GalleryDetail = lazy(() => import('./pages/Admin/GalleryDetail'));
const BookingManagement = lazy(() => import('./pages/Admin/BookingManagement'));
const OrderManagement = lazy(() => import('./pages/Admin/OrderManagement'));
const FinancialReports = lazy(() => import('./pages/Admin/FinancialReports'));
const SettingsPage = lazy(() => import('./pages/Admin/SettingsPage'));
const TaskSystemManagement = lazy(() => import('./pages/Admin/TaskSystemManagement'));

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Always check auth status when app loads
    // This will verify stored credentials are still valid
    dispatch(checkAuthStatus());
  }, [dispatch]);

  return (
    <>
      {/* Floor only. Every public page sets its own via components/Common/Seo,
          which overrides this because Helmet dedupes tags it owns. index.html
          deliberately carries no description or og:* -- static copies there
          cannot be deduped and won, so pages advertised the generic text. */}
      <Helmet>
        <title>Vidhi Farm — Organic Farm & Farm Stay in Birbhum, West Bengal</title>
        <meta
          name="description"
          content="An organic permaculture farm in Birbhum, West Bengal. Farm-fresh produce, and weekend farm stays with Bengali home cooking."
        />
      </Helmet>
      
      <ScrollToTop />
      
      {/* Staff routes and /about are lazy, so they need a boundary. The
          fallback is deliberately plain: it shows for a chunk fetch only. */}
      <Suspense
        fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        }
      >
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/:productId" element={<ProductDetailPage />} />
          <Route path="gallery" element={<GalleryPage />} />
          <Route path="gallery/:galleryId" element={<GalleryViewPage />} />
          <Route path="experiences" element={<ExperiencesPage />} />
          <Route path="farm-stay" element={<FarmStayPage />} />
          <Route path="farm-stay/book" element={<FarmStayBookingPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="checkout" element={<SimpleCheckout />} />
          <Route path="booking" element={<BookingPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:id/:slug" element={<BlogDetailPage />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="confirm-signup" element={<ConfirmSignup />} />
        </Route>

        {/* Customer Protected Routes */}
        <Route path="/customer" element={
          <ProtectedRoute requiredRole="customer">
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<CustomerDashboard />} />
          <Route path="orders" element={<MyOrders />} />
          <Route path="bookings" element={<MyBookings />} />
          <Route path="profile" element={<CustomerProfile />} />
        </Route>

        {/* Worker Protected Routes */}
        <Route path="/worker" element={
          <ProtectedRoute requiredRole={['tree_specialist', 'crop_specialist', 'home_worker']}>
            <AdminLayout userType="worker" />
          </ProtectedRoute>
        }>
          <Route index element={<WorkerDashboard />} />
          <Route path="tasks" element={<TaskManager />} />
          <Route path="production" element={<ProductionTracker />} />
        </Route>

        {/* Admin Protected Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRole={['admin', 'manager']}>
            <AdminLayout userType="admin" />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="products" element={<ProductManagement />} />
          <Route path="products/create" element={<ProductForm />} />
          <Route path="products/edit/:id" element={<ProductForm />} />
          <Route path="products/view/:id" element={<ProductForm />} />
          <Route path="blogs" element={<BlogManagement />} />
          <Route path="blogs/create" element={<BlogEditPage />} />
          <Route path="blogs/edit/:id" element={<BlogEditPage />} />
          <Route path="newsletter" element={<NewsletterManagement />} />
          <Route path="landing" element={<LandingManagement />} />
          <Route path="featured-products" element={<FeaturedProductsManagement />} />
          <Route path="categories" element={<CategoryManagement />} />
          <Route path="galleries" element={<GalleryManagement />} />
          <Route path="galleries/:galleryId" element={<GalleryDetail />} />
          <Route path="bookings" element={<BookingManagement />} />
          <Route path="orders" element={<OrderManagement />} />
          <Route path="task-system" element={<TaskSystemManagement />} />
          <Route path="tasks" element={<TaskManager />} />
          <Route path="task-templates" element={<WeeklyTaskTemplate />} />
          <Route path="machinery" element={<MachineryManagement />} />
          <Route path="machinery/dashboard" element={<MachineryDashboard />} />
          <Route path="machinery/:id" element={<MachineryDetail />} />
          <Route path="reports" element={<FinancialReports />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* 404 Route */}
        <Route path="*" element={
          <Layout>
            <div style={{ textAlign: 'center', padding: '100px 20px' }}>
              <h2>Page Not Found</h2>
              <p>The page you're looking for doesn't exist.</p>
            </div>
          </Layout>
        } />
      </Routes>
      </Suspense>
    </>
  );
}

export default App;